import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  BarChart3, MessagesSquare, Users2, AlertTriangle, Sparkles, Loader2, TrendingUp,
  FileUp,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  BarChart, Bar,
} from "recharts";

const RANGES = [
  { value: "7", label: "Últimos 7 dias" },
  { value: "30", label: "Últimos 30 dias" },
  { value: "90", label: "Últimos 90 dias" },
];

function pct(n) {
  return `${(Number(n || 0) * 100).toFixed(1)}%`;
}

function Kpi({ icon: Icon, label, value, hint, tone = "default" }) {
  const toneMap = {
    default: "text-slate-700 bg-slate-100",
    warning: "text-amber-700 bg-amber-100",
    success: "text-emerald-700 bg-emerald-100",
    primary: "text-[#1e5a8e] bg-[#e6f0fa]",
  };
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</p>
          <p className="font-[Outfit] text-3xl font-semibold text-slate-900 mt-2 tracking-tight">{value}</p>
          {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

export default function AnalyticsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const navigate = useNavigate();

  const [days, setDays] = useState("30");
  const [sectorId, setSectorId] = useState(isAdmin ? "" : (user?.sector_id || ""));
  const [sectors, setSectors] = useState([]);
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  function suggestDocument(question) {
    const base = isAdmin ? "/admin/documents" : "/coordinator/documents";
    const params = new URLSearchParams({ suggest: question });
    if (isAdmin && sectorId) params.set("sector_id", sectorId);
    navigate(`${base}?${params.toString()}`);
  }

  useEffect(() => {
    if (isAdmin) {
      api.get("/sectors").then(({ data }) => {
        setSectors(data);
        if (!sectorId && data.length > 0) setSectorId(data[0].id);
      });
    }
  }, []);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        if (isAdmin) {
          const { data: ov } = await api.get("/analytics/overview", { params: { days } });
          setOverview(ov);
        }
        if (sectorId) {
          const { data: d } = await api.get(`/analytics/sector/${sectorId}`, { params: { days } });
          setData(d);
        } else {
          setData(null);
        }
      } finally { setLoading(false); }
    }
    load();
  }, [sectorId, days, isAdmin]);

  const sectorOptions = useMemo(() => sectors, [sectors]);

  return (
    <div>
      <PageHeader
        title="Analytics"
        subtitle={
          isAdmin
            ? "Visão consolidada de conversas, taxa de fallback e perguntas frequentes por setor."
            : "Como o chatbot do seu setor está performando."
        }
        action={
          <div className="flex gap-3">
            {isAdmin && sectors.length > 0 && (
              <Select value={sectorId} onValueChange={setSectorId}>
                <SelectTrigger className="w-56" data-testid="analytics-sector-filter">
                  <SelectValue placeholder="Selecione setor" />
                </SelectTrigger>
                <SelectContent>
                  {sectorOptions.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-44" data-testid="analytics-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RANGES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {loading && !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : (
        <Tabs defaultValue={isAdmin ? "overview" : "sector"} className="space-y-6">
          <TabsList>
            {isAdmin && <TabsTrigger value="overview" data-testid="tab-overview">Visão geral</TabsTrigger>}
            <TabsTrigger value="sector" data-testid="tab-sector">Setor selecionado</TabsTrigger>
          </TabsList>

          {isAdmin && (
            <TabsContent value="overview" className="space-y-6">
              {!overview || overview.totals.messages === 0 ? (
                <EmptyState
                  icon={BarChart3}
                  title="Sem dados ainda"
                  description="Ainda não houve conversas nos últimos dias. Assim que o widget for usado, as métricas aparecem aqui."
                />
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Kpi icon={MessagesSquare} label="Mensagens" value={overview.totals.messages} tone="primary" />
                    <Kpi icon={Users2} label="Sessões únicas" value={overview.totals.sessions} tone="success" />
                    <Kpi icon={Sparkles} label="Respondidas" value={overview.totals.answered} tone="success" />
                    <Kpi icon={AlertTriangle} label="Taxa de fallback" value={pct(overview.totals.fallback_rate)} tone="warning" hint={`${overview.totals.fallback} sem contexto`} />
                  </div>

                  <Card className="p-6">
                    <h3 className="font-[Outfit] font-semibold text-slate-900 mb-4">Uso por setor</h3>
                    <div className="h-72">
                      <ResponsiveContainer>
                        <BarChart data={overview.per_sector}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="sector_name" tick={{ fill: "#64748b", fontSize: 12 }} />
                          <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                          <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                          <Legend />
                          <Bar dataKey="answered" name="Respondidas" fill="#1e5a8e" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="fallback" name="Fallback" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>

                  <Card className="overflow-hidden">
                    <div className="px-6 py-4 border-b border-slate-100">
                      <h3 className="font-[Outfit] font-semibold text-slate-900">Ranking de setores</h3>
                    </div>
                    <table className="w-full text-left">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Setor</th>
                          <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Mensagens</th>
                          <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Sessões</th>
                          <th className="px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Fallback %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overview.per_sector.map((s) => (
                          <tr key={s.sector_id} className="border-b border-slate-100 last:border-0">
                            <td className="px-6 py-3 text-sm text-slate-900 font-medium">{s.sector_name}</td>
                            <td className="px-6 py-3 text-sm text-slate-600">{s.messages}</td>
                            <td className="px-6 py-3 text-sm text-slate-600">{s.sessions}</td>
                            <td className="px-6 py-3 text-sm">
                              <Badge className={s.fallback_rate > 0.4 ? "bg-amber-100 text-amber-700 hover:bg-amber-100" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100"}>
                                {pct(s.fallback_rate)}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Card>
                </>
              )}
            </TabsContent>
          )}

          <TabsContent value="sector" className="space-y-6">
            {!data ? (
              <EmptyState
                icon={BarChart3}
                title="Selecione um setor"
                description={isAdmin ? "Escolha um setor no filtro acima para ver as métricas detalhadas." : "Você ainda não está vinculado a um setor."}
              />
            ) : data.totals.messages === 0 ? (
              <EmptyState
                icon={TrendingUp}
                title={`Sem conversas em ${data.sector.name}`}
                description="Assim que o widget for usado, as métricas aparecem aqui — perguntas mais frequentes, taxa de fallback e tendência ao longo dos dias."
              />
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Kpi icon={MessagesSquare} label="Mensagens" value={data.totals.messages} tone="primary" />
                  <Kpi icon={Users2} label="Sessões" value={data.totals.sessions} tone="success" />
                  <Kpi icon={AlertTriangle} label="Fallback" value={pct(data.totals.fallback_rate)} tone="warning" hint={`${data.totals.fallback} sem contexto`} />
                  <Kpi icon={Sparkles} label="Similaridade média" value={data.totals.avg_similarity.toFixed(2)} tone="default" hint="0-1, apenas respondidas" />
                </div>

                <Card className="p-6">
                  <h3 className="font-[Outfit] font-semibold text-slate-900 mb-4">Conversas ao longo do tempo</h3>
                  <div className="h-72">
                    <ResponsiveContainer>
                      <AreaChart data={data.timeseries}>
                        <defs>
                          <linearGradient id="areaAnswered" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#1e5a8e" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#1e5a8e" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="areaFallback" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 12 }} />
                        <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                        <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                        <Legend />
                        <Area type="monotone" dataKey="answered" name="Respondidas" stroke="#1e5a8e" fill="url(#areaAnswered)" strokeWidth={2} />
                        <Area type="monotone" dataKey="fallback" name="Fallback" stroke="#f59e0b" fill="url(#areaFallback)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <QuestionList
                    title="Perguntas sem resposta (oportunidade)"
                    subtitle="Melhore a base de conhecimento adicionando documentos que respondam essas perguntas."
                    items={data.top_fallback_questions}
                    tone="warning"
                    testId="top-fallback"
                    onSuggest={suggestDocument}
                  />
                  <QuestionList
                    title="Top perguntas respondidas"
                    subtitle="O que os usuários mais estão perguntando (e o chatbot conseguiu responder)."
                    items={data.top_answered_questions}
                    tone="primary"
                    testId="top-answered"
                  />
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function QuestionList({ title, subtitle, items, tone, testId, onSuggest }) {
  const bgTone = tone === "warning" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-[#e6f0fa] text-[#1e5a8e] border-[#cce0f5]";
  return (
    <Card className="p-6" data-testid={testId}>
      <h3 className="font-[Outfit] font-semibold text-slate-900">{title}</h3>
      <p className="text-xs text-slate-500 mt-1 mb-4">{subtitle}</p>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400 py-4 text-center">Nenhuma pergunta neste período.</p>
      ) : (
        <ol className="space-y-3">
          {items.map((q, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <span className={`shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-xs font-semibold ${bgTone}`}>
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-slate-700 leading-relaxed">{q.question}</p>
                {onSuggest && (
                  <button
                    type="button"
                    onClick={() => onSuggest(q.question)}
                    data-testid={`suggest-doc-${i}`}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-[#1e5a8e] hover:text-[#164570] hover:underline focus:outline-none focus:ring-2 focus:ring-[#1e5a8e]/30 rounded"
                  >
                    <FileUp className="w-3 h-3" />
                    Sugerir documento
                  </button>
                )}
              </div>
              <span className="text-xs text-slate-400 shrink-0">{q.count}×</span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
